import React from 'react'
import { Address } from 'app-types'
import { FormattedMessage } from 'react-intl'

import { codetableContains } from './helpers'
import { CodeText } from '../redux/reducers/codetable/model'
import * as dateHelper from './dates'
import messages from '../i18n'
import { SecondDocumentType } from '../constants/enums'

export const isEmpty = (value: any) => value === undefined || value === null || value === ''
const checkMobilePhone = (value: any) => !/^(60[1-8]|7([02379])[0-9])[0-9]{6}$/i.test(value)
const checkEmail = (value: any) =>
  !/^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[A-Z0-9-]+\.)+[A-Z]{2,6}$/i.test(
    value
  )
const checkBirthNumber = (value: any) => /^[0-9]{6}\/?[0-9]{4}$/i.test(value)
// regex for birth numbers for people born before 1954
const checkBirthNumber53 = (value: any) => /^[0-9]{6}\/?[0-9]{3}$/i.test(value)

const checkDrivingLicense = (value: any) => /^[A-Z]{2}[ ]?[0-9]{6}$/i.test(value)

const SAFE_INT = 9007199254740991

export const required = (value: string | undefined) => {
  if (isEmpty(value)) {
    return <FormattedMessage {...messages.validation.required} />
  }
  return undefined
}

export const atLeastOne = (value: boolean) => {
  if (!value) {
    return <FormattedMessage {...messages.validation.atLeastOne} />
  }
  return undefined
}

export const isChecked = (value: string, message?: any) => {
  if (isEmpty(value) || !value) {
    return <FormattedMessage {...(message || messages.validation.mustBeChecked)} />
  }
  return undefined
}

export const minLength = (min: number) => (value: string) => {
  if (!isEmpty(value) && value.length < min) {
    return <FormattedMessage {...messages.validation.mustBeAtLeast} values={{ min }} />
  }
  return undefined
}

export const maxLength = (max: number) => (value: string | number) =>
  !isEmpty(value) && `${value}`.length > max ? (
    <FormattedMessage {...messages.validation.mustBeNoMoreThan} values={{ max }} />
  ) : undefined

export const hasLength = (length: number) => (value: string) => {
  if (value.length !== length) {
    return (
      <FormattedMessage
        {...messages.validation.mustBeCharactersLong}
        values={{
          length,
        }}
      />
    )
  }
  return undefined
}

export const hasNotSpecialCharacters = (value: string) =>
  /[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~]/i.test(value) ? (
    <FormattedMessage {...messages.validation.hasNotSpecialCharacters} />
  ) : undefined

export const isNumber = (value: any) => {
  if (isNaN(parseFloat(value)) || !isFinite(value)) {
    return <FormattedMessage {...messages.validation.mustBeNumeric} />
  }
  return undefined
}

export const isInteger = (value: any) => {
  const isNumberError = isNumber(value)
  if (isNumberError) {
    return isNumberError
  }

  const num = Number(value)
  if (num !== Math.floor(num) || Math.abs(num) > SAFE_INT) {
    return <FormattedMessage {...messages.validation.mustBeInteger} />
  }
  return undefined
}

export const nonNegativeNumber = (value: string) => {
  const isNumberError = isNumber(value)
  if (isNumberError) {
    return isNumberError
  }

  if (Number(value) < 0) {
    return <FormattedMessage {...messages.validation.mustBePositiveOrZero} />
  }
  return undefined
}

export const positiveNumber = (value: string) => {
  const isNumberError = isNumber(value)
  if (isNumberError) {
    return isNumberError
  }

  if (Number(value) <= 0) {
    return <FormattedMessage {...messages.validation.mustBePositive} />
  }
  return undefined
}

export const moreThan = (limit: number) => (value: number) => {
  if (value <= limit) {
    return <FormattedMessage {...messages.validation.moreThan} values={{ limit }} />
  }
  return undefined
}

export const moreThanOrEqualTo = (limit: number) => (value: number) => {
  if (value < limit) {
    return <FormattedMessage {...messages.validation.moreThanOrEqualTo} values={{ limit }} />
  }
  return undefined
}

export const moreThanOrEqualToField = (value: number, limit: number, message: any) => {
  if (value < limit) {
    return <FormattedMessage {...message} values={{ limit: '' }} />
  }
  return undefined
}

export const lessThanOrEqualTo = (limit: number) => (value: number) => {
  if (value > limit) {
    return <FormattedMessage {...messages.validation.value_must_be_less_than} values={{ limit }} />
  }
  return undefined
}

export const withinCodetable = (code: string, table: CodeText[]) => {
  if (!table) {
    return undefined
  }
  const found = codetableContains(table, code)
  return found ? undefined : <FormattedMessage {...messages.validation.mustBeInTable} />
}

export const validDate = (date: string) => {
  if (!dateHelper.isCzechDate(date)) {
    return <FormattedMessage {...messages.validation.invalidDate} />
  }
  return undefined
}

export const validDateInFuture = (date: string) => {
  const dateError = validDate(date)
  if (dateError) {
    return dateError
  }

  if (dateHelper.isCzechDateAfter(date)) {
    return <FormattedMessage {...messages.validation.mustBeInTheFuture} />
  }
  return undefined
}

export const validDateInPast = (date: string) => {
  const dateError = validDate(date)
  if (dateError) {
    return dateError
  }
  if (dateHelper.isCzechDateBefore(date)) {
    return <FormattedMessage {...messages.validation.mustBeInThePast} />
  }
  return undefined
}

export const isPhone = (value: string) => {
  if (checkMobilePhone(value)) {
    return <FormattedMessage {...messages.validation.invalidPhoneNumber} />
  }
  return undefined
}

export const isEmail = (value: string) => {
  if (isEmpty(value) || checkEmail(value)) {
    return <FormattedMessage {...messages.validation.invalidEmailAddress} />
  }
  return undefined
}

export const idCardNumber = (value: string) =>
  required(value) || positiveNumber(value) || hasLength(9)(value)

const getRCpart = (rc: string, part: string): number => {
  switch (part) {
    case 'year':
      let year = parseInt(rc.substr(0, 2), 10)
      // sadly we need to guess complete birth year
      year += year < 54 && rc.length === 10 ? 2000 : 1900
      return year
    case 'month':
      let month = parseInt(rc.substr(2, 2), 10)
      // a little adjustment for yearly adopters :)
      if (month > 70 && getRCpart(rc, 'year') > 2003) {
        month -= 70
      } else if (month > 50) {
        month -= 50
      } else if (month > 20 && getRCpart(rc, 'year') > 2003) {
        month -= 20
      }
      return month
    case 'day':
      return parseInt(rc.substr(4, 2), 10)
    case 'ext':
      return parseInt(rc.substr(6, 4), 10)
    default:
      return 0
  }
}

const mod11RC = (rc: string): boolean => {
  // lets do some MOD 11 magic
  let c = 0
  if (rc.length === 10) {
    c = parseInt(rc.substr(9, 1), 10)
  }
  let m = parseInt(rc.substr(0, 9), 10) % 11
  if (m === 10) {
    m = 0
  }
  if (m !== c) {
    return false
  }
  return true
}

const olderThan18 = (rc: string): boolean => {
  const date: any = new Date()
  const birthDate: any = new Date(
    getRCpart(rc, 'year') + 18,
    getRCpart(rc, 'month') - 1,
    getRCpart(rc, 'day')
  )
  if (birthDate - date > 0) {
    return false
  }
  return true
}

const validMonthDayRC = (rc: string): boolean => {
  if (getRCpart(rc, 'month') === 0 || getRCpart(rc, 'month') > 12) {
    return false
  }
  if (getRCpart(rc, 'day') === 0 || getRCpart(rc, 'day') > 31) {
    return false
  }
  return true
}

const validateRC = (rc: string, age: number = 0): boolean => {
  const date = new Date()

  if (rc.length === 0) {
    return true
  }
  // validation for birth year < 1954 => no validation :)
  if (rc.length === 9 && getRCpart(rc, 'year') < 1954) {
    return true
  }
  // validate mod 11
  if (!mod11RC(rc)) {
    return false
  }
  // validate if is born
  if (getRCpart(rc, 'year') + age > date.getFullYear()) {
    return false
  }
  // validate if older than 18
  if (!olderThan18(rc)) {
    return false
  }
  // check valid date
  if (!validMonthDayRC(rc)) {
    return false
  }

  return true
}

export const birthNumber = (value: string) => {
  if (value === undefined || value === null) {
    return undefined
  }

  if (value.substr(0, 2) < '54') {
    // user can input 9 or 10 number (Y2K bug)
    if (!(checkBirthNumber53(value) || checkBirthNumber(value))) {
      return <FormattedMessage {...messages.validation.invalidBirthNumber} />
    }
  } else if (!checkBirthNumber(value)) {
    return <FormattedMessage {...messages.validation.invalidBirthNumber} />
  }
  if (!validateRC(value.replace('/', ''))) {
    return <FormattedMessage {...messages.validation.invalidBirthNumber} />
  }
  return undefined
}

export const verifyEmploymentId = (value: string) => {
  // check if is not 8 chars long
  if (!/^\d{8}$/.test(value)) {
    return <FormattedMessage {...messages.validation.invalidEmploymentId} />
  }

  // control summary
  const sum = value
    .split('') //  split string to arr
    .map((item, index) => Number(item) * (8 - index)) //  multiply each number
    //  create summary excluding last digit
    .reduce((total, current, index) => (index === 7 ? total : total + current))

  const rest = sum % 11
  let lastDigit = null

  if (rest === 0) {
    lastDigit = 1
  } else if (rest === 1) {
    lastDigit = 0
  } else {
    lastDigit = 11 - rest
  }

  if (Number(value[7]) !== lastDigit) {
    return <FormattedMessage {...messages.validation.invalidEmploymentId} />
  }
  return undefined
}

export const justNumber = (value: string) => {
  if (/[^\d]/.test(value)) {
    return (
      <FormattedMessage
        id="validation.must_include_only_numbers"
        defaultMessage="Must include only numbers"
      />
    )
  }
  return undefined
}

export const drivingLicenseOrPassport = (documentType: string, value: string) => {
  if (SecondDocumentType.DRIVING_LICENSE === documentType) {
    return drivingLicense(value)
  } else if (SecondDocumentType.PASSPORT === documentType) {
    return passport(value)
  }
  return undefined
}

export const drivingLicense = (value: string) => {
  if (!checkDrivingLicense(value)) {
    return (
      <FormattedMessage
        id="validation.driving_license"
        defaultMessage="Invalid driving license number"
      />
    )
  }
  return undefined
}

export const passport = (value: string) => justNumber(value) || minLength(8)(value)

export const isStreet = (value: string) =>
  ',#-/ !@$%^*(){}|[].'.indexOf(value) >= 0 ? (
    <FormattedMessage id="validation.invalid_street" defaultMessage="Invalid street" />
  ) : undefined

export const isHouseNumber = (value: string) =>
  !/^[1-9][a-zA-Z0-9]*$/i.test(value) ? (
    <FormattedMessage
      id="validation.must_include_only_letters_numbers"
      defaultMessage="Must include only text characters"
    />
  ) : undefined

export const isCity = (value: string) =>
  /[.,/#!$%?^&@§+*;:{}=\\_`"'~()<>|[\]]/i.test(value) ? (
    <FormattedMessage {...messages.validation.invalidCity} />
  ) : undefined

export const isZip = (value: string) =>
  !/^[1-7]{1}[0-9]{2}\s?[0-9]{2}$/i.test(value) ? (
    <FormattedMessage {...messages.validation.invalidZip} />
  ) : undefined

export const addressValidations = (value?: Address) => {
  const { street = '', city = '', zip = '', landRegistryNumber = '', houseNumber = '' } =
    value || {}
  return {
    street: required(street) || isStreet(street),
    city: required(city) || isCity(city),
    zip: required(zip) || isZip(zip),
    landRegistryNumber: required(landRegistryNumber) || isInteger(landRegistryNumber),
    houseNumber: houseNumber === '' ? undefined : isHouseNumber(houseNumber),
  }
}

export const stringLength = (min: number, max: number) => (value: string) =>
  minLength(min)(value) || maxLength(max)(value)
